import {
  ArchiveIcon,
  HomeIcon,
  type Icon,
  LinkIcon,
  MirrorIcon,
  OrganizationIcon,
  RepoForkedIcon,
  RepoIcon,
  RepoLockedIcon,
  RepoTemplateIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {Box, Button, NavList, Text} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useState} from 'react'

import type {TypeFilter} from '../types/repos-list-types'

interface TypesCollapsedButtonProps extends TypesNavListProps {
  listTitle: string
}

export function TypesCollapsedButton(props: TypesCollapsedButtonProps) {
  const [isTypesDialogOpen, showTypesDialog] = useState(false)

  return (
    <>
      <Button
        variant="invisible"
        onClick={() => showTypesDialog(true)}
        trailingVisual={TriangleDownIcon}
        aria-describedby="types-collapsed-button-description"
        sx={{ml: -2, mb: 2}}
      >
        <Box as="h1" sx={{fontSize: 3}}>
          {`${props.listTitle} repositories`}
        </Box>
      </Button>
      <Text className="sr-only" id="types-collapsed-button-description" aria-live="polite">
        Change the repository type filter
      </Text>
      {isTypesDialogOpen && <TypesDialog {...props} onClose={() => showTypesDialog(false)} />}
    </>
  )
}

interface TypesDialogProps extends TypesNavListProps {
  onClose(): void
}

function TypesDialog({type, types, onClose}: TypesDialogProps) {
  return (
    <Dialog onClose={onClose} title="Types" width="small">
      <TypesNavList type={type} types={types} />
    </Dialog>
  )
}

interface TypesNavListProps {
  type: string
  types: TypeFilter[]
}

export function TypesNavList({type, types}: TypesNavListProps) {
  return (
    <NavList>
      {types.map(({id, text}) => (
        <NavList.Item key={id} aria-current={type === id && 'page'} href={`?type=${id}`}>
          <NavList.LeadingVisual>{getTypeIcon(id)}</NavList.LeadingVisual>
          {text}
        </NavList.Item>
      ))}
    </NavList>
  )
}

const iconClassByType: Record<string, Icon> = {
  all: HomeIcon,
  public: RepoIcon,
  internal: OrganizationIcon,
  private: RepoLockedIcon,
  source: LinkIcon,
  fork: RepoForkedIcon,
  mirror: MirrorIcon,
  template: RepoTemplateIcon,
  archived: ArchiveIcon,
}

function getTypeIcon(type: string) {
  const IconComponent = iconClassByType[type]!
  return <IconComponent />
}

try{ TypesCollapsedButton.displayName ||= 'TypesCollapsedButton' } catch {}
try{ TypesDialog.displayName ||= 'TypesDialog' } catch {}
try{ TypesNavList.displayName ||= 'TypesNavList' } catch {}